import React, { useEffect, useRef, useState } from 'react';
import { OnChangeValue } from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { isNumber, isString, uniqBy } from 'lodash';
import { Query, QueryContactTagsArgs } from '@lgg/isomorphic/types/__generated__/graphql';
import { useContactTagListForSelect } from 'src/components/domain/contacts/hooks/use-contact-tag-list-for-select';
import {
  CONTACT_TAGS,
  ContactTagRefetchType,
} from 'src/components/domain/contacts/hooks/use-contact-tags';
import { entityToSelectOptionMapper } from 'src/components/general/inputs/select/mappers/entity-to-select-option-mapper';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

type ContactTagSelectorProps<IsMulti extends boolean> = {
  selectedTagsIds: number[];
  isMulti: IsMulti;
  onChange: (existingTagIds: number[], newTagNames: string[]) => void;
  isCreatable?: boolean;
  name: string;
  placeholder?: string;
  mobileLabel?: string;
  label?: string;
  isLoading?: boolean;
  refetchRef?: (refetch: ContactTagRefetchType) => void;
  triggerOverride?: (
    value: OnChangeValue<SelectOption<number>, IsMulti>,
  ) => React.ReactNode;
  multiValueMaxToShow?: number;
  renderValue?: (value: OnChangeValue<SelectOption<number>, IsMulti>) => React.ReactNode;
  renderPlaceholder?: () => React.ReactNode;
  isClearable?: boolean;
  hideIndicators?: boolean;
};

export const ContactTagSelect = <IsMulti extends boolean>(
  props: ContactTagSelectorProps<IsMulti>,
) => {
  const currentInstitution = useCurrentInstitution();
  const {
    isMulti,
    selectedTagsIds,
    isLoading,
    isCreatable,
    refetchRef,
    triggerOverride,
    mobileLabel,
    renderValue,
    renderPlaceholder,
    multiValueMaxToShow,
    isClearable,
    hideIndicators,
    ...rest
  } = props;
  const [cachedTagOptions, setCachedTagOptions] = useState<SelectOption<number>[]>([]);
  const [pendingNewTagOptions, setPendingNewTagOptions] = useState<
    SelectOption<string>[]
  >([]);
  const [fetchExtraTags] = useLazyQuery<
    Pick<Query, 'contactTags'>,
    Partial<QueryContactTagsArgs>
  >(CONTACT_TAGS);

  // Tracks whether a search was performed while the menu was open, so closing it
  // without having searched doesn't trigger a redundant refetch with unchanged variables.
  const hasSearchedRef = useRef(false);

  // On first render we load the selected tags, for cases where the input first query
  // does not return the already selected tags
  useEffect(() => {
    void (async () => {
      if (!selectedTagsIds.length) {
        return;
      }

      const { data } = await fetchExtraTags({
        variables: {
          institutionId: currentInstitution.id,
          where: { id: { _in: selectedTagsIds }, isActive: { _eq: true } },
        },
      });

      const nodes = getNodesFromConnection(data?.contactTags);

      if (nodes) {
        setCachedTagOptions((prevState) => {
          return uniqBy(
            [...prevState, ...nodes.map(entityToSelectOptionMapper)],
            'value',
          );
        });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { tagOptions, loadingTagOptions, refetch, loadMore, hasNextPage } =
    useContactTagListForSelect();

  useEffect(() => {
    if (tagOptions.length) {
      setCachedTagOptions((prevState) => {
        return uniqBy([...prevState, ...tagOptions], 'value');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTagOptions]);

  useEffect(() => {
    refetchRef?.(refetch);
  }, [refetch, refetchRef]);

  // Once a pending tag actually exists as a real tag (e.g. a consumer persisted it and
  // refetched), drop it from the pending list so it doesn't render as a duplicate chip.
  useEffect(() => {
    setPendingNewTagOptions((prevState) => {
      if (!prevState.length) {
        return prevState;
      }

      return prevState.filter(
        (pendingTag) => !cachedTagOptions.some((tag) => tag.label === pendingTag.label),
      );
    });
  }, [cachedTagOptions]);

  return (
    <Select
      {...rest}
      hideIndicators={hideIndicators}
      isSearchable
      isMulti={isMulti}
      isTag
      onChange={(value) => {
        const selectedOptions = (
          value ? (Array.isArray(value) ? value : [value]) : []
        ) as SelectOption<number | string>[];

        const existingTagIds = selectedOptions
          .filter((option): option is SelectOption<number> => isNumber(option.value))
          .map((option) => option.value as number);

        const newTagOptions = selectedOptions.filter(
          (option): option is SelectOption<string> => isString(option.value),
        ) as SelectOption<string>[];

        setPendingNewTagOptions(uniqBy(newTagOptions, 'value'));

        rest?.onChange(
          existingTagIds,
          newTagOptions.map((option) => option.label),
        );
      }}
      onMenuClose={async () => {
        if (!hasSearchedRef.current) {
          return;
        }

        hasSearchedRef.current = false;
        await refetch({
          where: {
            isActive: { _eq: true },
          },
        });
      }}
      onSearch={async (name) => {
        hasSearchedRef.current = true;
        await refetch({ where: { name: { _contains: name }, isActive: { _eq: true } } });
      }}
      canLoadMore={hasNextPage}
      onLoadMore={loadMore}
      isLoading={loadingTagOptions || isLoading}
      isCreatable={isCreatable}
      isClearable={isClearable}
      mobileLabel={mobileLabel}
      renderPlaceholder={renderPlaceholder}
      multiValueMaxToShow={multiValueMaxToShow}
      value={
        [
          ...(selectedTagsIds
            .map((id) => {
              return cachedTagOptions.find((tag) => tag.value === id);
            })
            .filter(Boolean) as SelectOption<number>[]),
          ...pendingNewTagOptions,
        ] as SelectOption<number>[]
      }
      options={[...pendingNewTagOptions, ...tagOptions] as SelectOption<number>[]}
    />
  );
};
